import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useBlob } from '../hooks/useBlob';
import { useUserBlob, UserBlobErrorBanner } from '../hooks/useUserBlob';

// Phase 12: per-user product-detail edits now persist to commercial.UserBlob.
// Legacy localStorage key kept for one-shot migration on first hit.
const USER_BLOB_KEY = 'product-details-v1';
const LEGACY_LS_KEY = 'product-details-v1';
const BRAND_PILLS = ['All', 'Bhuja', 'Infuzions', 'Other'];
const KA_ACCOUNTS = ['Metcash / ALM', 'TIR', 'Coles', 'Woolworths', 'SPAR', 'Drakes', 'Big W', 'ALDI', 'Costco'];

function ImagePanel({ images }) {
  const views = [
    images?.front && { label: 'Front', src: images.front },
    images?.back  && { label: 'Back',  src: images.back },
    ...(images?.extra || []).map((src, i) => ({ label: `View ${i + 2}`, src })),
  ].filter(Boolean);

  const [idx, setIdx] = React.useState(0);
  const [lightbox, setLightbox] = React.useState(false);

  if (!views.length) {
    return (
      <div style={{ width: 140, height: 140, flexShrink: 0, background: 'var(--border)', border: '1px dashed var(--border-soft)', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <div style={{ fontSize: 24 }}>📦</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 10 }}>No render</div>
      </div>
    );
  }

  const current = views[Math.min(idx, views.length - 1)];

  return (
    <>
      <div style={{ width: 140, flexShrink: 0 }}>
        {/* Main image */}
        <div
          onClick={() => setLightbox(true)}
          style={{ width: 140, height: 140, background: 'var(--border)', borderRadius: 8, overflow: 'hidden', cursor: 'zoom-in', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', marginBottom: 6 }}
          title="Click to enlarge"
        >
          <img
            src={current.src}
            alt={current.label}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>
        {/* Toggle buttons */}
        {views.length > 1 && (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
            {views.map((v, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                style={{
                  background: idx === i ? 'var(--info-soft)' : 'var(--border)',
                  border: `1px solid ${idx === i ? 'var(--info-border)' : 'var(--border-soft)'}`,
                  borderRadius: 4, color: idx === i ? 'var(--info)' : 'var(--text-muted)',
                  padding: '2px 8px', fontSize: 10, cursor: 'pointer',
                }}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox overlay */}
      {lightbox && (
        <div
          onClick={() => setLightbox(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img src={current.src} alt={current.label} style={{ maxWidth: '80vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} />
            {/* Nav arrows */}
            {views.length > 1 && (
              <>
                <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + views.length) % views.length); }} style={{ position: 'absolute', left: -48, top: '50%', transform: 'translateY(-50%)', background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 24, color: 'var(--text-primary)', width: 36, height: 36, cursor: 'pointer', fontSize: 16 }}>‹</button>
                <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % views.length); }} style={{ position: 'absolute', right: -48, top: '50%', transform: 'translateY(-50%)', background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 24, color: 'var(--text-primary)', width: 36, height: 36, cursor: 'pointer', fontSize: 16 }}>›</button>
              </>
            )}
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>{current.label} · click anywhere to close</div>
          </div>
        </div>
      )}
    </>
  );
}

function sv(v) { return v || ''; }

function Field({ label, value, editing, field, onEdit, type = 'text', wide }) {
  const inputStyle = {
    background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 4,
    color: 'var(--text-primary)', fontSize: 12, padding: '3px 7px',
    width: wide ? '100%' : 140,
  };
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 10, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      {editing ? (
        type === 'textarea'
          ? <textarea
              value={value || ''}
              onChange={e => onEdit(field, e.target.value)}
              rows={3}
              style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
            />
          : <input
              type={type === 'number' ? 'number' : 'text'}
              value={value ?? ''}
              onChange={e => onEdit(field, type === 'number' ? (e.target.value === '' ? null : parseFloat(e.target.value)) : e.target.value)}
              style={inputStyle}
            />
      ) : (
        <div style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 13, whiteSpace: wide ? 'pre-wrap' : 'normal', wordBreak: 'break-word' }}>
          {value || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>}
        </div>
      )}
    </div>
  );
}

function KACodesTable({ codes, editing, onEdit }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {KA_ACCOUNTS.map(ka => (
        <div key={ka} style={{ background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: 10, marginBottom: 4, textTransform: 'uppercase' }}>{ka}</div>
          {editing
            ? <input
                type="text"
                value={codes?.[ka] || ''}
                onChange={e => onEdit(ka, e.target.value || null)}
                placeholder="—"
                style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-primary)', fontSize: 12, padding: '2px 6px', width: '100%' }}
              />
            : <div style={{ color: codes?.[ka] ? 'var(--text-primary)' : 'var(--text-muted)', fontFamily: 'monospace', fontSize: 13 }}>
                {codes?.[ka] || '—'}
              </div>
          }
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 16, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'var(--border)', border: 'none', padding: '10px 16px', textAlign: 'left', color: 'var(--text-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
      >
        {title}
        <span style={{ color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: '12px 16px' }}>{children}</div>}
    </div>
  );
}

function ProductCard({ product, overrides, onSave, onDelete, images }) {
  const rec = { ...product, ...overrides };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  function startEdit() {
    setDraft(JSON.parse(JSON.stringify(rec)));
    setEditing(true);
  }

  function cancelEdit() { setEditing(false); setDraft(null); }

  function commitEdit() {
    onSave(draft);
    setEditing(false);
    setDraft(null);
  }

  function editField(field, val) {
    setDraft(d => ({ ...d, [field]: val }));
  }

  function editKA(account, val) {
    setDraft(d => ({
      ...d,
      ka_codes: { ...(d.ka_codes || {}), [account]: val },
    }));
  }

  const current = editing ? draft : rec;
  const hasKA = current.ka_codes && Object.keys(current.ka_codes).filter(k => current.ka_codes[k]).length > 0;

  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 };

  const brand = current.brand || '';
  const brandColor = brand === 'Bhuja' ? '#d97706' : brand === 'Infuzions' ? '#7c3aed' : 'var(--text-muted)';

  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 16 }}>
      {/* Product header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Image panel */}
        <ImagePanel images={images} />

        {/* Product info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ background: 'var(--border)', color: brandColor, borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{current.brand}</span>
                <span style={{ background: 'var(--border)', color: 'var(--text-muted)', borderRadius: 12, padding: '2px 8px', fontSize: 10 }}>Item #{current.item_num}</span>
                {images && <span style={{ background: 'var(--success-soft)', color: 'var(--success)', borderRadius: 12, padding: '2px 8px', fontSize: 10 }}>📷 Render</span>}
                {current.deleted === 'Y' && <span style={{ background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 12, padding: '2px 8px', fontSize: 10 }}>Deleted</span>}
              </div>
              {editing
                ? <input
                    value={draft.description || ''}
                    onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                    style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, padding: '4px 10px', width: 400 }}
                  />
                : <div style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700 }}>{current.description}</div>
              }
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>{current.assortment} · {current.size}</div>
              {current.rrp && (
                <div style={{ color: 'var(--text-body)', fontSize: 12, marginTop: 2 }}>
                  RRP <strong style={{ color: 'var(--text-primary)' }}>${Number(current.rrp).toFixed(2)}</strong>
                  {current.wholesale_ex_gst && <span style={{ marginLeft: 12 }}>Wholesale <strong style={{ color: 'var(--text-primary)' }}>${Number(current.wholesale_ex_gst).toFixed(2)}</strong> ex-GST</span>}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {editing
                ? <>
                    <button onClick={commitEdit} style={{ background: 'var(--success)', border: 'none', borderRadius: 5, color: '#fff', padding: '5px 14px', fontSize: 12, cursor: 'pointer' }}>Save</button>
                    <button onClick={cancelEdit} style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  </>
                : <button onClick={startEdit} style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-body)', padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Edit</button>
              }
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '14px 16px' }}>
        {/* Key Account Codes */}
        <Section title="Key Account Product Codes">
          <KACodesTable codes={current.ka_codes || {}} editing={editing} onEdit={editing ? editKA : () => {}} />
        </Section>

        {/* Identifiers & Pricing */}
        <Section title="Identifiers & Pricing">
          <div style={gridStyle}>
            <Field label="Unit Barcode" value={sv(current.unit_barcode)} editing={editing} field="unit_barcode" onEdit={editField} />
            <Field label="Case/Shipper Barcode" value={sv(current.case_barcode)} editing={editing} field="case_barcode" onEdit={editField} />
            <Field label="Wholesale Ex GST" value={current.wholesale_ex_gst != null ? `$${Number(current.wholesale_ex_gst).toFixed(2)}` : ''} editing={editing} field="wholesale_ex_gst" type="number" onEdit={editField} />
            <Field label="Australian RRP" value={current.rrp != null ? `$${Number(current.rrp).toFixed(2)}` : ''} editing={editing} field="rrp" type="number" onEdit={editField} />
            <Field label="Date Available" value={sv(current.date_available)} editing={editing} field="date_available" onEdit={editField} />
          </div>
        </Section>

        {/* Packing Info */}
        <Section title="Packing Information">
          <div style={gridStyle}>
            <Field label="Shipper Qty" value={current.shipper_qty != null ? String(current.shipper_qty) : ''} editing={editing} field="shipper_qty" type="number" onEdit={editField} />
            <Field label="Pallet Config" value={sv(current.pallet_config)} editing={editing} field="pallet_config" onEdit={editField} />
            <Field label="Unit Net Weight" value={sv(current.unit_net_weight)} editing={editing} field="unit_net_weight" onEdit={editField} />
            <Field label="Unit Gross Weight" value={sv(current.unit_gross_weight)} editing={editing} field="unit_gross_weight" onEdit={editField} />
            <Field label="Carton Net Weight (kg)" value={current.carton_net_weight_kg != null ? String(current.carton_net_weight_kg) : ''} editing={editing} field="carton_net_weight_kg" type="number" onEdit={editField} />
            <Field label="Carton Gross Weight (kg)" value={current.carton_gross_weight_kg != null ? String(current.carton_gross_weight_kg) : ''} editing={editing} field="carton_gross_weight_kg" type="number" onEdit={editField} />
            <Field label="Pallet Net Weight (kg)" value={current.pallet_net_weight_kg != null ? String(current.pallet_net_weight_kg) : ''} editing={editing} field="pallet_net_weight_kg" type="number" onEdit={editField} />
            <Field label="Pallet Gross Weight (kg)" value={current.pallet_gross_weight_kg != null ? String(current.pallet_gross_weight_kg) : ''} editing={editing} field="pallet_gross_weight_kg" type="number" onEdit={editField} />
            <Field label="Unit Dimensions (mm)" value={sv(current.unit_dimensions_mm)} editing={editing} field="unit_dimensions_mm" onEdit={editField} />
            <Field label="Case Dimensions (mm)" value={sv(current.case_dimensions_mm)} editing={editing} field="case_dimensions_mm" onEdit={editField} />
            <Field label="Pallet Dimensions (mm)" value={sv(current.pallet_dimensions_mm)} editing={editing} field="pallet_dimensions_mm" onEdit={editField} />
          </div>
        </Section>

        {/* Storage & Supply */}
        <Section title="Storage, Supply & Origin">
          <div style={gridStyle}>
            <Field label="Storage Conditions" value={sv(current.storage_conditions)} editing={editing} field="storage_conditions" onEdit={editField} />
            <Field label="Transport Requirements" value={sv(current.transport_requirements)} editing={editing} field="transport_requirements" onEdit={editField} />
            <Field label="Lead Time" value={sv(current.lead_time)} editing={editing} field="lead_time" onEdit={editField} />
            <Field label="Shelf Life (days)" value={current.shelf_life_days != null ? String(current.shelf_life_days) : ''} editing={editing} field="shelf_life_days" type="number" onEdit={editField} />
            <Field label="Australian Made" value={sv(current.australian_made)} editing={editing} field="australian_made" onEdit={editField} />
            <Field label="Country of Origin" value={sv(current.country_of_origin)} editing={editing} field="country_of_origin" onEdit={editField} />
            <Field label="Aus. Content %" value={current.australian_content_pct != null ? `${(current.australian_content_pct * 100).toFixed(0)}%` : ''} editing={editing} field="australian_content_pct" type="number" onEdit={editField} />
            <Field label="Suitable For" value={sv(current.suitable_for)} editing={editing} field="suitable_for" onEdit={editField} wide />
          </div>
        </Section>

        {/* Content */}
        <Section title="Ingredients & Marketing">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Ingredients" value={sv(current.ingredients)} editing={editing} field="ingredients" type="textarea" onEdit={editField} wide />
            <Field label="Warning Copy" value={sv(current.warning_copy)} editing={editing} field="warning_copy" type="textarea" onEdit={editField} wide />
            <Field label="Marketing Message" value={sv(current.marketing_message)} editing={editing} field="marketing_message" type="textarea" onEdit={editField} wide />
            <Field label="Romance Text" value={sv(current.romance_text)} editing={editing} field="romance_text" type="textarea" onEdit={editField} wide />
          </div>
        </Section>
      </div>
    </div>
  );
}

function newProduct() {
  return {
    item_num: `NEW-${Date.now()}`,
    brand: 'Bhuja',
    assortment: '',
    description: '',
    size: '',
    unit_barcode: '',
    case_barcode: '',
    ka_codes: {},
    _new: true,
  };
}

export default function ProductDetails() {
  const { data: productInfoBlob, isLoading } = useBlob('product-info');
  const { data: productImagesBlob } = useBlob('product-images');
  const userBlob = useUserBlob(USER_BLOB_KEY);
  const baseData = productInfoBlob?.products || [];
  const [overrides, setOverrides] = useState({});
  const [additions, setAdditions] = useState([]);
  const hydratedRef = useRef(false);
  const loading = isLoading;

  const [brandFilter, setBrandFilter] = useState('All');
  const [skuFilter, setSkuFilter] = useState('All');
  const [searchFilter, setSearchFilter] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [saved, setSaved] = useState(false);
  const [imageMap, setImageMap] = useState({});

  // Phase 6: product-info + product-images now come from /api/blob (was /data/*.json).
  useEffect(() => {
    if (productImagesBlob) setImageMap(productImagesBlob);
  }, [productImagesBlob]);

  // Hydrate per-user edits — server-side per-user blob → legacy localStorage → empty.
  useEffect(() => {
    if (userBlob.data === undefined) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    if (userBlob.data && (userBlob.data.overrides || userBlob.data.additions)) {
      setOverrides(userBlob.data.overrides || {});
      setAdditions(userBlob.data.additions || []);
      return;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(LEGACY_LS_KEY) || '{}');
      const ovs = stored.overrides || {};
      const adds = stored.additions || [];
      setOverrides(ovs);
      setAdditions(adds);
      if (Object.keys(ovs).length > 0 || adds.length > 0) {
        userBlob.save({ overrides: ovs, additions: adds });
        try { localStorage.removeItem(LEGACY_LS_KEY); } catch {}
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBlob.data]);

  function saveStorage(newOverrides, newAdditions) {
    const payload = { overrides: newOverrides ?? overrides, additions: newAdditions ?? additions };
    userBlob.scheduleSave(payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  function handleSave(updated) {
    const newOverrides = { ...overrides, [updated.item_num]: updated };
    if (updated._new) {
      const newAdds = additions.map(a => a.item_num === updated.item_num ? updated : a);
      setAdditions(newAdds);
      saveStorage(newOverrides, newAdds);
    } else {
      setOverrides(newOverrides);
      saveStorage(newOverrides, null);
    }
  }

  function handleAddNew() {
    const p = newProduct();
    const newAdds = [...additions, p];
    setAdditions(newAdds);
    saveStorage(null, newAdds);
  }

  const allProducts = useMemo(() => {
    const merged = baseData.map(p => ({ ...p, ...(overrides[p.item_num] || {}) }));
    return [...merged, ...additions];
  }, [baseData, overrides, additions]);

  // Assortment options scoped to current brand selection
  const assortmentOptions = useMemo(() => {
    let ps = allProducts;
    if (brandFilter === 'Bhuja') ps = ps.filter(p => p.brand === 'Bhuja');
    else if (brandFilter === 'Infuzions') ps = ps.filter(p => p.brand === 'Infuzions');
    else if (brandFilter === 'Other') ps = ps.filter(p => p.brand !== 'Bhuja' && p.brand !== 'Infuzions');
    const a = new Set(ps.map(p => p.assortment).filter(Boolean));
    return ['All', ...Array.from(a).sort()];
  }, [allProducts, brandFilter]);

  // SKU options scoped to current brand + assortment selection
  const skuOptions = useMemo(() => {
    let ps = allProducts;
    if (!showDeleted) ps = ps.filter(p => p.deleted !== 'Y');
    if (brandFilter === 'Bhuja') ps = ps.filter(p => p.brand === 'Bhuja');
    else if (brandFilter === 'Infuzions') ps = ps.filter(p => p.brand === 'Infuzions');
    else if (brandFilter === 'Other') ps = ps.filter(p => p.brand !== 'Bhuja' && p.brand !== 'Infuzions');
    if (skuFilter !== 'All') ps = ps.filter(p => p.assortment === skuFilter);
    return ps.map(p => ({ item_num: p.item_num, label: `${p.item_num} — ${p.description || ''}` }));
  }, [allProducts, brandFilter, skuFilter, showDeleted]);

  const [itemFilter, setItemFilter] = useState('All');

  // Reset assortment and item filter when brand changes; item filter when assortment changes
  useEffect(() => { setSkuFilter('All'); setItemFilter('All'); }, [brandFilter]);
  useEffect(() => { setItemFilter('All'); }, [skuFilter]);

  const filtered = useMemo(() => {
    let ps = allProducts;
    if (!showDeleted) ps = ps.filter(p => p.deleted !== 'Y');
    if (brandFilter === 'Bhuja') ps = ps.filter(p => p.brand === 'Bhuja');
    else if (brandFilter === 'Infuzions') ps = ps.filter(p => p.brand === 'Infuzions');
    else if (brandFilter === 'Other') ps = ps.filter(p => p.brand !== 'Bhuja' && p.brand !== 'Infuzions');
    if (skuFilter !== 'All') ps = ps.filter(p => p.assortment === skuFilter);
    if (itemFilter !== 'All') ps = ps.filter(p => p.item_num === itemFilter);
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      ps = ps.filter(p =>
        p.description?.toLowerCase().includes(q) ||
        p.item_num?.includes(q) ||
        p.assortment?.toLowerCase().includes(q)
      );
    }
    return ps;
  }, [allProducts, brandFilter, skuFilter, itemFilter, searchFilter, showDeleted]);

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading product data…</div>;

  const labelStyle = { color: 'var(--text-muted)', fontSize: 11, marginBottom: 4 };

  return (
    <div>
      {/* Load-failure banner — silent-fail bug fix (Phase 15). */}
      <UserBlobErrorBanner blob={userBlob} noun="product detail edits" hydrated={hydratedRef.current} />
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>Product Details</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
            {filtered.length} products · Source: Product Information.xlsx · saved to the database (per-user)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && <span style={{ color: 'var(--success)', fontSize: 12 }}>Saved</span>}
          <button
            onClick={handleAddNew}
            style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 6, color: 'var(--info)', padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
          >
            + Add Product
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          ['Total SKUs', allProducts.filter(p => p.deleted !== 'Y').length],
          ['Bhuja SKUs', allProducts.filter(p => p.brand === 'Bhuja' && p.deleted !== 'Y').length],
          ['Infuzions SKUs', allProducts.filter(p => p.brand === 'Infuzions' && p.deleted !== 'Y').length],
          ['With KA Codes', allProducts.filter(p => p.ka_codes && Object.keys(p.ka_codes).length > 0).length],
        ].map(([label, value]) => (
          <div key={label} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
            <div style={labelStyle}>{label}</div>
            <div style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <div>
            <div style={labelStyle}>Brand</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {BRAND_PILLS.map(b => (
                <button
                  key={b}
                  onClick={() => setBrandFilter(b)}
                  style={{
                    background: brandFilter === b ? 'var(--info-soft)' : 'var(--border)',
                    border: `1px solid ${brandFilter === b ? 'var(--info-border)' : 'var(--border-soft)'}`,
                    borderRadius: 12,
                    color: brandFilter === b ? 'var(--info)' : 'var(--text-muted)',
                    padding: '3px 12px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginLeft: 8 }}>
            <div style={labelStyle}>Search</div>
            <input
              type="text"
              placeholder="Name or item #…"
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, padding: '4px 8px', width: 180 }}
            />
          </div>
        </div>
        {/* Assortment filter row */}
        <div style={{ marginTop: 10 }}>
          <div style={{ color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Assortment</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {assortmentOptions.map(a => (
              <button
                key={a}
                onClick={() => setSkuFilter(a)}
                style={{
                  background: skuFilter === a ? 'var(--info-soft)' : 'var(--border)',
                  border: `1px solid ${skuFilter === a ? 'var(--info)' : 'var(--border)'}`,
                  borderRadius: 12,
                  color: skuFilter === a ? 'var(--info)' : 'var(--text-muted)',
                  padding: '2px 10px', fontSize: 11, cursor: 'pointer',
                }}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
        {/* SKU filter row */}
        <div style={{ marginTop: 10 }}>
          <div style={{ color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>SKU</div>
          <select
            value={itemFilter}
            onChange={e => setItemFilter(e.target.value)}
            style={{ background: 'var(--border)', border: `1px solid ${itemFilter !== 'All' ? 'var(--info)' : 'var(--border)'}`, borderRadius: 6, color: itemFilter !== 'All' ? 'var(--info)' : 'var(--text-muted)', padding: '4px 10px', fontSize: 12, cursor: 'pointer', maxWidth: 340 }}
          >
            <option value="All">All SKUs</option>
            {skuOptions.map(s => (
              <option key={s.item_num} value={s.item_num}>{s.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              id="showDeleted"
              checked={showDeleted}
              onChange={e => setShowDeleted(e.target.checked)}
              style={{ accentColor: 'var(--info)' }}
            />
            <label htmlFor="showDeleted" style={{ color: 'var(--text-body)', fontSize: 12, cursor: 'pointer' }}>Show deleted</label>
          </div>
        </div>
      </div>

      {/* Product cards */}
      {filtered.map(product => (
        <ProductCard
          key={product.item_num}
          product={baseData.find(p => p.item_num === product.item_num) || product}
          overrides={overrides[product.item_num] || (product._new ? product : {})}
          onSave={handleSave}
          images={imageMap[product.item_num] || null}
        />
      ))}

      {filtered.length === 0 && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>No products match the current filters.</div>
      )}
    </div>
  );
}
